/**
 * **投资页**（S 域 · 第 19 域 · 批 `99` B2）
 *
 * 依据：`投资域PRD_持仓与买卖` **v2** §4.2（三段：回报率汇总 / 持仓台账 / 动作区）·
 * §8（16 AC）· `DR-059` D1（独立页 + 导航 12 项）· D4（年化与持有天数同屏）·
 * D7（历史 39 分口径差如实标注）· 铁律 8（红涨绿跌）· 24（防双实现）· 36（间距取令牌）。
 *
 * ## 🔴 页面只做三件事
 *
 * 1. **取数一次**（`GET /api/investments/overview`）：持仓 + 六个汇总数 + 三档回报率 + 年化；
 * 2. **把展示判断交给 `features/investment.ts`**（分组、文本、动作文案都已单测钉住）；
 * 3. **摆四个入口**（买入 / 卖出 / 记收益 / 改价）+ 页头两个（买入 / 批量改价）。
 *
 * ## 🔴 `AC-INV13`：页面上的每个数字都必须等于接口值
 *
 * 汇总卡**直接渲染 `byCurrency[i]` 的字段**，台账行直接渲染 `HoldingView` 的字段 ——
 * 全页**没有任何 `reduce` / 加总**。端侧一旦自己加，就会出现"卡片合计 ¥966,052.09、
 * 明细加起来 ¥966,052.15"这种没有任何人能解释的差。
 *
 * ## ⚠️ hook 顺序（本页踩过两次的坑）
 *
 * 所有 `useState` / `useQuery` 必须排在 `if (isPending) return …` **之前**：
 * 首帧走早退出分支时 hook 数量少一个 ⇒ React #310 ⇒ 整页白屏（`AccountsPage` 有先例）。
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  AccountType,
  formatMoneyText,
  toCents,
  type CurrencySummaryView,
  type HoldingView,
} from '@jiaying/shared';
import { getInvestmentOverview, getInvestmentReconcile, listAccounts } from '../api/endpoints.js';
import { localToday } from '../app/local-day.js';
import { percentWithSign } from '../features/percent-text.js';
import {
  groupByPlatform,
  hasClosingDiff,
  isCleared,
  isThinSample,
  xirrHintOf,
  type TradeMode,
} from '../features/investment.js';
import {
  BTN_BATCH_PRICE,
  CLOSING_DIFF_HINT,
  EMPTY_HOLDINGS_TEXT,
  LEDGER_HINT,
  LEDGER_TITLE,
  PAGE_BUY,
  PAGE_SUBTITLE,
  PAGE_TITLE,
  SUMMARY_DIVIDEND,
  SUMMARY_INVESTED,
  SUMMARY_MARKET_VALUE,
  SUMMARY_PROCEEDS,
  SUMMARY_REALIZED,
  SUMMARY_REALIZED_RETURN,
  SUMMARY_TITLE,
  SUMMARY_TOTAL_RETURN,
  SUMMARY_UNREALIZED,
  SUMMARY_XIRR,
  XIRR_NONE_TEXT,
} from '../features/investment-labels.js';
import { Loading } from '../ui/Loading.js';
import { Amount } from '../ui/Amount.js';
import { Grid } from '../ui/Grid.js';
import { HoldingRow } from '../ui/HoldingRow.js';
import { InvestmentReconcileCard } from '../ui/InvestmentReconcileCard.js';
import { InvestmentTradeDialog } from '../ui/InvestmentTradeDialog.js';
import { InvestmentTradesDialog } from '../ui/InvestmentTradesDialog.js';
import { BatchPriceDialog, HoldingPriceDialog, InvestmentIncomeDialog } from '../ui/InvestmentDialogs.js';
import type { AccountPickerItemWithCurrency } from '../ui/AccountPicker.js';
import { INVESTMENTS_HIDE_CLEARED_KEY, readUiFlag, writeUiFlag } from '../features/ui-prefs.js';

/** 买卖对话框的打开状态（`holding === null` = 新建买入） */
interface TradeTarget {
  readonly mode: TradeMode;
  readonly holding: HoldingView | null;
}

export function InvestmentsPage(): ReactElement {
  const theme = useTheme();
  const today = localToday();

  const overview = useQuery({ queryKey: ['investment-overview'], queryFn: getInvestmentOverview });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  /**
   * 🆕 批 99 B3：报表口径对账（**独立接口**，不塞进 overview —— 它是"核对"，
   * 不是页面主体，失败时也不该让整页打不开）。
   */
  const reconcile = useQuery({
    queryKey: ['investment-reconcile'],
    queryFn: () => getInvestmentReconcile(),
  });

  // ── 四个入口的状态（🔴 全部排在早退出之前）──
  const [trade, setTrade] = useState<TradeTarget | null>(null);
  const [incomeId, setIncomeId] = useState<string | null>(null);
  const [priceId, setPriceId] = useState<string | null>(null);
  /** 🆕 批 127：交易记录（撤销入口） */
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);
  /**
   * 🆕 批 `128`：隐藏已清仓（`AC-INV7` **不变** —— 数据上"不删行"仍然成立，
   * 这里只是**显示层**开关，且默认值可随时拨回来）。
   *
   * 🔴 为什么必须留开关而不是干脆不显示：持仓被卖光的行是**唯一**能回答
   * "我卖过的基金去哪儿了 / 那笔收益记在哪"的地方（`HoldingRow` 文件头原话）。
   * 隐藏可以，但必须**找得回来**。
   */
  const [hideCleared, setHideCleared] = useState(() => readUiFlag(INVESTMENTS_HIDE_CLEARED_KEY, true));

  const notice = (theme.jiaying.tokens['c-notice'] ?? theme.palette.warning.main) as string;

  if (overview.isPending) return <Loading />;
  if (overview.isError) {
    return <Alert severity="error">投资数据读取失败：{String(overview.error)}</Alert>;
  }

  const holdings = overview.data.holdings;
  const summaries = overview.data.byCurrency;
  /**
   * 🆕 批 `128`：已清仓 = `quantityE4 === 0`（`isCleared`，与 `AC-INV7` 同一判据）。
   * 只影响**台账显示**：汇总卡 / 口径对账仍用服务端全量数（清仓行市值恒 0，本就不进市值）。
   */
  const clearedCount = holdings.filter((h) => isCleared(h)).length;
  const shown = hideCleared ? holdings.filter((h) => !isCleared(h)) : holdings;
  const groups = groupByPlatform(shown);

  /**
   * 资金账户候选：**只留投资类**（`AC-INV1` 的端侧那一半；终审在服务端）。
   * 🔴 类型用 `AccountPickerItemWithCurrency` —— 投资买卖要按币种校验（批 `115`）。
   */
  const investAccounts: AccountPickerItemWithCurrency[] = (accounts.data?.items ?? [])
    .filter((a) => a.type === AccountType.Investment)
    .map((a) => ({ id: a.id, name: a.name, ownerName: a.ownerName, currency: a.currency }));

  const incomeTarget = incomeId === null ? null : (holdings.find((h) => h.id === incomeId) ?? null);
  const priceTarget = priceId === null ? null : (holdings.find((h) => h.id === priceId) ?? null);
  const historyTarget = historyId === null ? null : (holdings.find((h) => h.id === historyId) ?? null);
  /** 批量改价只列还在持有的（已清仓市值恒 0，改价没有意义） */
  const repriceable = holdings.filter((h) => !isCleared(h));

  return (
    <Stack spacing={2} data-testid="investments-page">
      {/* ── 页头 ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {PAGE_TITLE}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {PAGE_SUBTITLE}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            onClick={() => setBatchOpen(true)}
            disabled={repriceable.length === 0}
            data-testid="invest-batch-price"
          >
            {BTN_BATCH_PRICE}
          </Button>
          <Button
            variant="contained"
            onClick={() => setTrade({ mode: 'buy', holding: null })}
            disabled={investAccounts.length === 0}
            data-testid="invest-buy"
          >
            {PAGE_BUY}
          </Button>
        </Stack>
      </Stack>

      {/* 资金账户还没读到时给一句，而不是让「买入」静默变灰 */}
      {investAccounts.length === 0 && (
        <Alert severity="info" data-testid="invest-no-account">
          还没有「投资」类账户 —— 买卖的现金要有落点，先去「账户」页建一个投资账户。
        </Alert>
      )}

      {/* ── ① 回报率汇总（每个币种一张卡，不跨币相加 —— DR-004）── */}
      {summaries.map((s) => (
        <SummaryCard key={s.currency} summary={s} notice={notice} />
      ))}

      {/* ── ② 持仓台账（平台 → 品种）── */}
      {holdings.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" data-testid="invest-empty">
              {EMPTY_HOLDINGS_TEXT}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              {LEDGER_TITLE}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {LEDGER_HINT}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {/* 只在真有清仓行时出现 —— 没有清仓时它是个不起作用的控件 */}
            {clearedCount === 0 ? null : (
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={hideCleared}
                    onChange={(e) => {
                      setHideCleared(e.target.checked);
                      writeUiFlag(INVESTMENTS_HIDE_CLEARED_KEY, e.target.checked);
                    }}
                    data-testid="invest-hide-cleared"
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    {hideCleared ? `已折叠 ${clearedCount} 笔已清仓` : '隐藏已清仓'}
                  </Typography>
                }
              />
            )}
          </Stack>
          {shown.length === 0 ? (
            /* 持仓全被卖光且已折叠 —— 说清"不是没有"，并给回退动作 */
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" data-testid="invest-all-cleared">
                  这 {clearedCount} 笔持仓都已清仓，已按你的设置折叠。打开上面的开关可以查看（历史收益与清仓价都在里面）。
                </Typography>
              </CardContent>
            </Card>
          ) : (
            groups.map((g) => (
              <Card key={g.key} data-testid={`invest-group-${g.key}`}>
                <CardContent sx={{ pb: 0.5 }}>
                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {g.accountName}（{g.holdings.length}）
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Chip size="small" variant="outlined" label={g.currency} />
                  </Stack>
                </CardContent>
                <Divider />
                {g.holdings.map((h) => (
                  <HoldingRow
                    key={h.id}
                    view={h}
                    today={today}
                    onBuy={() => setTrade({ mode: 'buy', holding: h })}
                    onSell={() => setTrade({ mode: 'sell', holding: h })}
                    onIncome={() => setIncomeId(h.id)}
                    onPrice={() => setPriceId(h.id)}
                    onHistory={() => setHistoryId(h.id)}
                  />
                ))}
              </Card>
            ))
          )}
        </>
      )}

      {/* ── ③ 口径对账（报表 vs 投资记录 · 批 99 B3）── */}
      {reconcile.isSuccess && (
        <InvestmentReconcileCard reconcile={reconcile.data} noticeColor={notice} />
      )}
      {reconcile.isError && (
        <Alert severity="info" data-testid="invest-reconcile-error">
          口径对账读取失败（不影响上面的数字）：{String(reconcile.error)}
        </Alert>
      )}

      {/* ── ④ 动作区（对话框）── */}
      {trade !== null && (
        <InvestmentTradeDialog
          mode={trade.mode}
          holding={trade.holding}
          accounts={investAccounts}
          today={today}
          onClose={() => setTrade(null)}
        />
      )}
      {incomeTarget !== null && (
        <InvestmentIncomeDialog
          holding={incomeTarget}
          today={today}
          onClose={() => setIncomeId(null)}
        />
      )}
      {priceTarget !== null && (
        <HoldingPriceDialog holding={priceTarget} onClose={() => setPriceId(null)} />
      )}
      {historyTarget !== null && (
        <InvestmentTradesDialog holding={historyTarget} onClose={() => setHistoryId(null)} />
      )}
      {batchOpen && (
        <BatchPriceDialog holdings={repriceable} onClose={() => setBatchOpen(false)} />
      )}
    </Stack>
  );
}

/**
 * 一个币种的汇总卡 —— **六个数字全部直接读接口字段**（`AC-INV13`）。
 *
 * 顺序按 PRD §4.2 原型：总投入 / 当前市值 / 已实现盈亏 / 累计分红 / 浮盈 / 总收益率，
 * 第二行「已实现收益率 · 年化（带持有天数）」。
 */
function SummaryCard({
  summary: s,
  notice,
}: {
  summary: CurrencySummaryView;
  notice: string;
}): ReactElement {
  const c = s.currency;
  return (
    <Card data-testid={`invest-summary-${c}`}>
      <CardContent sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {SUMMARY_TITLE}
          </Typography>
          <Chip size="small" variant="outlined" label={c} />
          <Box sx={{ flexGrow: 1 }} />
          {/* 🔴 历史 39 分口径差如实标注（DR-059 D7）—— 不修正、不摊平 */}
          {hasClosingDiff(s) && (
            <Typography variant="caption" sx={{ color: notice }} data-testid={`invest-diff-${c}`}>
              口径差 {formatMoneyText(toCents(s.closingDiffCents), c, { signed: true })} ·{' '}
              {CLOSING_DIFF_HINT}
            </Typography>
          )}
        </Stack>

        <Grid cols={3}>
          <Metric label={SUMMARY_INVESTED} cents={s.investedCents} currency={c} />
          <Metric label={SUMMARY_MARKET_VALUE} cents={s.marketValueCents} currency={c} />
          <Metric label={SUMMARY_REALIZED} cents={s.realizedPnlCents} currency={c} signed />
          <Metric label={SUMMARY_DIVIDEND} cents={s.dividendCents} currency={c} signed />
          <Metric label={SUMMARY_UNREALIZED} cents={s.unrealizedCents} currency={c} signed />
          <Metric label={SUMMARY_PROCEEDS} cents={s.proceedsCents} currency={c} />
        </Grid>

        <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <Typography variant="body2" color="text.secondary">
            {SUMMARY_TOTAL_RETURN}{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {s.totalReturnE4 === null ? '—' : percentWithSign(s.totalReturnE4)}
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {SUMMARY_REALIZED_RETURN}{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {s.realizedReturnE4 === null ? '—' : percentWithSign(s.realizedReturnE4)}
            </Box>
          </Typography>

          {/* 🔴 年化必须与「持有 N 天 · 样本不足」同屏（AC-INV12）；`null` 不显示成 0% */}
          {s.xirr === null ? (
            <Typography variant="body2" color="text.secondary" data-testid={`invest-xirr-none-${c}`}>
              {SUMMARY_XIRR} {XIRR_NONE_TEXT}
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography
                variant="body2"
                data-testid={`invest-xirr-${c}`}
                // 🔴 样本 < 90 天用 notice 琥珀，不用 alert 朱红（铁律 8：这不是错误）
                sx={{ color: isThinSample(s.xirr) ? notice : 'text.primary', fontWeight: 600 }}
              >
                {SUMMARY_XIRR} {percentWithSign(s.xirr.pctE4)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {xirrHintOf(s.xirr)}
              </Typography>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

/** 一格"标签 + 金额"（金额一律走 `Amount`：带币种、等宽、正负着色） */
function Metric({
  label,
  cents,
  currency,
  signed = false,
}: {
  label: string;
  cents: number;
  currency: string;
  signed?: boolean;
}): ReactElement {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Amount
        cents={toCents(cents)}
        currency={currency}
        signed={signed}
        variant="body2"
        sx={{ fontWeight: 600 }}
      />
    </Box>
  );
}
